// Bloque de características (atributos) por producto — gemelo de
// `MedidasProductoBlock`: se inserta en el header de cada grupo de "Revisá tu
// catálogo" cuando el modo es catálogo de productos / infografía. Ver ADR-813.
//
// Por qué existe: la IA ya extrae 2-4 características al subir el producto
// (categorizeProduct → variantes.atributos). Antes solo se podían tocar desde
// el sheet de "Personalizar infografía"; el jefe pidió el mismo ATAJO que el de
// medidas, acá en el review, para que aparezcan las que dedujo la IA (o las que
// el usuario ya editó) y se puedan cambiar en el momento.
//
// Como el de medidas, es CONTROLADO: no guarda ni debouncea. El padre mantiene
// el estado (`atributosPorProducto`) y persiste con debounce vía
// `saveAtributosDeVariante`. Aplica a TODAS las categorías: una herramienta o un
// foco también tienen características, no solo los muebles.

import React from 'react';
import { Plus, Sparkles, X } from 'lucide-react';

export interface AtributosProductoBlockProps {
  atributos: string[];
  onChange: (next: string[]) => void;
  /** Tope de características. La IA sugiere hasta este número; el prompt de la
   *  infografía dibuja entre 2 y 4 pills, así que el default es 4. */
  maxAtributos?: number;
  /** Id del wrapper (por si más adelante hace falta scroll/anchor). */
  anchorId?: string;
  /** Modo embebido: sin el marco externo (borde/fondo/col-span). Se usa para
   *  anidar el bloque dentro de la tira de producto del modo Infografía, donde
   *  el marco lo pone el contenedor. Default false = card suelta de siempre. */
  embedded?: boolean;
}

export const AtributosProductoBlock: React.FC<AtributosProductoBlockProps> = ({
  atributos,
  onChange,
  maxAtributos = 4,
  anchorId,
  embedded = false,
}) => {
  const lista = atributos ?? [];

  const updateAt = (i: number, v: string) => {
    const next = [...lista];
    next[i] = v;
    onChange(next);
  };
  const removeAt = (i: number) => onChange(lista.filter((_, j) => j !== i));
  const addEmpty = () => onChange([...lista, '']);

  // Se puede sumar otra si no se llegó al tope y la última casilla no quedó
  // vacía sin completar (evita spamear inputs vacíos apretando el botón).
  const puedeAgregar =
    lista.length < maxAtributos &&
    (lista.length === 0 || lista[lista.length - 1].trim() !== '');

  const inner = (
    <>
      <div className="flex items-center gap-2 mb-2.5">
        <Sparkles className="w-3.5 h-3.5 text-[#3C3C43]/50 shrink-0" />
        <span className="text-[13px] font-medium text-brand-black/80">
          Características
        </span>
        {/* El usuario tiene que saber que estas etiquetas las dedujo la IA
            mirando la foto, no las escribió él. */}
        <span className="text-[11px] text-[#3C3C43]/55 truncate">
          · sugeridas por IA, revisalas
        </span>
      </div>

      {/* 4 columnas: hasta `maxAtributos` (4) características + el botón "Añadir"
          entran en una sola fila. Cada casilla es una característica; el botón
          es la celda siguiente mientras quede lugar. En mobile van de a 2. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {lista.map((attr, i) => (
          <div key={i} className="min-w-0 relative">
            <input
              type="text"
              value={attr}
              maxLength={24}
              placeholder="Ej: Roble macizo"
              aria-label={`Característica ${i + 1}`}
              onChange={(e) => updateAt(i, e.target.value)}
              className="w-full h-11 rounded-xl border border-neutral-200 pl-3 pr-9 text-[14px] text-brand-black/90 placeholder:text-[#3C3C43]/30 outline-none transition-colors focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary"
            />
            <button
              type="button"
              onClick={() => removeAt(i)}
              aria-label="Quitar característica"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 w-7 h-7 inline-flex items-center justify-center rounded-full text-brand-black/35 hover:bg-black/[0.06] hover:text-brand-black/70 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {puedeAgregar && (
          <button
            type="button"
            onClick={addEmpty}
            className="h-11 rounded-xl border border-dashed border-neutral-300 hover:border-brand-primary/50 bg-white hover:bg-brand-primary/[0.03] inline-flex items-center justify-center gap-1.5 text-[13px] font-medium text-[#3C3C43]/60 hover:text-brand-primary transition-colors"
          >
            <Plus className="w-4 h-4" />
            Añadir
          </button>
        )}
      </div>
    </>
  );

  return embedded ? (
    <div id={anchorId} className="min-w-0">
      {inner}
    </div>
  ) : (
    <div
      id={anchorId}
      className="col-span-full rounded-2xl border border-neutral-200 bg-white p-3.5 mb-1"
    >
      {inner}
    </div>
  );
};

export default AtributosProductoBlock;
